import { useState } from 'react';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Mail, MapPin, Clock, Send, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      toast.success('Nachricht gesendet! Wir melden uns innerhalb von 24 Stunden.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'E-Mail',
      value: 'support@wedvaya.com',
      description: 'Für allgemeine Anfragen',
    },
    {
      icon: MapPin,
      title: 'Adresse',
      value: 'Berlin, Deutschland',
      description: 'WEDVAYA GmbH',
    },
    {
      icon: Clock,
      title: 'Antwortzeit',
      value: '< 24 Stunden',
      description: 'Montag bis Freitag',
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-secondary" data-testid="contact-page">
        {/* Hero */}
        <section className="bg-primary py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">
              Kontakt
            </h1>
            <p className="text-white/90 text-lg">
              Wir sind hier, um Ihnen zu helfen. Schreiben Sie uns!
            </p>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contact Info */}
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <Card key={index}>
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <info.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">{info.title}</h3>
                        <p className="text-foreground font-medium">{info.value}</p>
                        <p className="text-sm text-muted-foreground">{info.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* FAQ Link */}
                <Card className="bg-primary/5 border-primary/20">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">Häufige Fragen?</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Vielleicht finden Sie die Antwort bereits in unseren FAQ.
                    </p>
                    <Button variant="outline" asChild className="w-full">
                      <a href="/faq">Zu den FAQ</a>
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="font-serif text-2xl">Nachricht senden</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name">Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Ihr Name"
                            required
                            data-testid="contact-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">E-Mail *</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="ihre@email.de"
                            required
                            data-testid="contact-email"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject">Betreff *</Label>
                        <Select
                          value={formData.subject}
                          onValueChange={(value) => setFormData({ ...formData, subject: value })}
                        >
                          <SelectTrigger data-testid="contact-subject">
                            <SelectValue placeholder="Wählen Sie einen Betreff" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">Allgemeine Anfrage</SelectItem>
                            <SelectItem value="host">Frage als Gastgeber</SelectItem>
                            <SelectItem value="guest">Frage als Gast</SelectItem>
                            <SelectItem value="payment">Zahlung & Buchung</SelectItem>
                            <SelectItem value="technical">Technisches Problem</SelectItem>
                            <SelectItem value="press">Presse & Medien</SelectItem>
                            <SelectItem value="partnership">Partnerschaft</SelectItem>
                            <SelectItem value="other">Sonstiges</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Nachricht *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Wie können wir Ihnen helfen?"
                          rows={6}
                          required
                          data-testid="contact-message"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full rounded-full"
                        disabled={loading || !formData.subject}
                        data-testid="contact-submit"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Wird gesendet...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 h-4 w-4" />
                            Nachricht senden
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default ContactPage;
